import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginSuccess, clearGooglePrefillData } from '../../store/slices/authslice';
import axios from 'axios';
import { endpoints } from '../../api/api';
import TermsModal from '../Login/components/TermsModal';
import { useTermsConsent } from '../../hooks/useTermsConsent';

const SignupInfo: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { mobileNumber, googlePrefillData } = useAppSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  // Prefill form with Google data if available
  useEffect(() => {
    if (googlePrefillData) {
      setFormData({
        firstName: googlePrefillData.firstName || '',
        lastName: googlePrefillData.lastName || '',
        email: googlePrefillData.email || ''
      });
      // Clear prefill data after using it
      dispatch(clearGooglePrefillData());
    }
  }, [googlePrefillData, dispatch]);
  
  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const { showTerms, triggerTerms, handleAccept: handleTermsAccept, handleDecline: handleTermsDecline } = useTermsConsent({
    onAccept: () => navigate('/'),
    onDecline: () => navigate('/login'),
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {
      firstName: '',
      lastName: '',
      email: ''
    };
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return !newErrors.firstName && !newErrors.lastName && !newErrors.email;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError(null);

    axios.post(endpoints.completeProfile, {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email
    })
    .then((response) => {
      const { user } = response.data;

      // Update localStorage with complete user info
      localStorage.setItem('user', JSON.stringify(user));

      // Save user data to Redux and redirect to dashboard
      dispatch(loginSuccess({
        id: user.id,
        name: user.name || `${user.firstName} ${user.lastName}`,
        mobile: user.phoneNumber || mobileNumber,
        email: user.email,
        emailVerified: user.emailVerified || false
      }));
      // trackSignUp() already fired at OTP-verify time (Login.tsx) — the
      // actual account-creation moment. Calling it again here double-counted
      // every OTP signup as two CompleteRegistration events.
      triggerTerms();
    })
    .catch((error) => {
      setApiError(error?.data?.message || error.message || 'Failed to complete profile. Please try again.');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <>
    {showTerms && <TermsModal onAccept={handleTermsAccept} onDecline={handleTermsDecline} />}
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white shadow-lg rounded-2xl border border-gray-200 p-8 w-full max-w-md">
        <div className="flex items-center mb-8">
          <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center mr-3">
            <span className="text-white font-bold text-sm">i</span>
          </div>
          <span className="text-gray-900 text-xl font-semibold">
            IndiePress
          </span>
        </div>

        <h2 className="text-gray-900 text-2xl font-semibold mb-2">Complete Your Profile</h2>
        <p className="text-gray-600 text-sm mb-6">
          Please provide your details to continue
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              className={`w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border ${
                errors.firstName ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white`}
              placeholder="Enter your first name"
            />
            {errors.firstName && (
              <p className="text-red-600 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              className={`w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border ${
                errors.lastName ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white`}
              placeholder="Enter your last name"
            />
            {errors.lastName && (
              <p className="text-red-600 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full bg-gray-50 text-gray-900 px-3 py-2.5 rounded-md border ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 focus:bg-white`}
              placeholder="Enter your email address"
            />
            {errors.email && (
              <p className="text-red-600 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {apiError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-600 text-sm">{apiError}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-3 rounded-md font-semibold transition-colors"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Creating Account...
              </div>
            ) : (
              'Sign Up'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-500 text-xs">
            By signing up, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
    </>
  );
};

export default SignupInfo;